import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { SelectedSubproject } from '@shared/models';
import { ActivityStream } from '@shared/models/project-setup';
import { AutoSaveableComponent } from '@shared/models/component';
import { DprDetails } from '@shared/models/dpr';
import { FilterActivityStreamsForDisplayPipe } from '@dpr-details/pipes/filter-activity-streams-for-display.pipe';
import { RoutingService, ScreenSizeService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-activities-page',
  templateUrl: './dpr-activities-page.component.html',
  styleUrls: ['./dpr-activities-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprActivitiesPageComponent
  implements OnInit, AutoSaveableComponent
{
  selectedSubproject$ = new BehaviorSubject<SelectedSubproject>(null);
  dprDetails$ = new BehaviorSubject<DprDetails>(null);

  preselectedActivityStreamId: number;

  private activeAutoSaveableComponent: AutoSaveableComponent;

  constructor(
    private router: Router,
    private routingService: RoutingService,
    private activatedRoute: ActivatedRoute,
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade,
    private filterActivityStreamsForDisplayPipe: FilterActivityStreamsForDisplayPipe,
    protected screenSizeService: ScreenSizeService
  ) {}

  ngOnInit(): void {
    this.activatedRoute?.firstChild?.params.subscribe((params) => {
      this.preselectedActivityStreamId = +params.activityStreamId;
    });

    combineLatest([
      this.userDetailsFacade.query.selectedSubproject$,
      // this is needed for back and forth navigation between dprs as the dpr id is changed and conditions must be recomputed
      this.activatedRoute.parent.params.pipe(
        map((params) => params.dprDetailId),
        distinctUntilChanged(),
        switchMap((dprDetailId) =>
          this.offlineFacade.getDprDetails(dprDetailId)
        )
      ),
    ])
      .pipe(
        untilDestroyed(this),
        filter(([_, dprDetails]) => !!dprDetails),
        tap(([subproject, dprDetails]) => {
          this.selectedSubproject$.next(subproject);
          this.dprDetails$.next(dprDetails);
          const activityStreams = subproject?.data?.activityStreams;

          const preselectedActivityStream = this.preselectedActivityStreamId
            ? this.filterActivityStreamsForDisplayPipe.transform(
                activityStreams,
                dprDetails.dprActivities,
                dprDetails.dprParallelActivities,
                this.preselectedActivityStreamId
              )[0]
            : null;

          // This is necessary in order to automatically redirect to the first activity stream if its missing from the url
          if (activityStreams?.length && !preselectedActivityStream) {
            this.navigateToActivityStream(
              dprDetails.id?.toString(),
              preselectedActivityStream?.id?.toString() ??
                activityStreams[0].id?.toString()
            );
          }
        })
      )
      .subscribe();
  }

  autoSave(): void {
    if (this.activeAutoSaveableComponent) {
      this.activeAutoSaveableComponent.autoSave();
    }
  }

  autoSaveFromAdminChange(): Observable<boolean> {
    if (this.activeAutoSaveableComponent) {
      return this.activeAutoSaveableComponent.autoSaveFromAdminChange();
    }

    return of(false);
  }

  componentActivated(component: Component): void {
    const autoSaveableComponent = component as AutoSaveableComponent;
    if (
      autoSaveableComponent.autoSave &&
      autoSaveableComponent.autoSaveFromAdminChange
    ) {
      this.activeAutoSaveableComponent = component as AutoSaveableComponent;
    }
  }

  componentDeactivated(): void {
    this.activeAutoSaveableComponent = null;
  }

  trackActivityStream(_index: number, activityStream: ActivityStream): number {
    return activityStream?.id;
  }

  changePreselectedActivityStreamId(id: number): void {
    this.preselectedActivityStreamId = id;
  }

  navigateToActivityStream(dprDetailsId: string, streamId: string): void {
    const routerLink = [
      ...this.routingService.getDprDetailsUrl(dprDetailsId),
      'activities',
      streamId,
    ];

    this.router.navigate(routerLink, {
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
